import { createElement } from "./dom_utils.js";
import { SunIcon, MoonIcon } from "../icons.js";

function isValidEmail(email) {
  if (!email) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function isValidPhone(phone) {
  if (!phone) return false;
  const digitsOnly = phone.replace(/[\s\-()+ ]/g, "");
  return /^\d{4,15}$/.test(digitsOnly);
}

/** Convert ISO country code to flag emoji (e.g. "US" → 🇺🇸) */
function countryToFlag(code) {
  return [...code.toUpperCase()]
    .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join("");
}

/** Popular country codes shown at top of the list */
const POPULAR_COUNTRIES = [
  "US",
  "GB",
  "IN",
  "CA",
  "AU",
  "DE",
  "FR",
  "SG",
  "AE",
  "BR",
  "JP",
  "NL",
  "ES",
  "IT",
  "MX",
  "KR",
  "SA",
  "CH",
  "SE",
  "IE",
];

/** Fallback when libphonenumber-js is not available */
const FALLBACK_COUNTRIES = [
  { code: "US", dial: "+1", name: "United States", flag: countryToFlag("US") },
  {
    code: "GB",
    dial: "+44",
    name: "United Kingdom",
    flag: countryToFlag("GB"),
  },
  { code: "IN", dial: "+91", name: "India", flag: countryToFlag("IN") },
  { code: "CA", dial: "+1", name: "Canada", flag: countryToFlag("CA") },
  { code: "AU", dial: "+61", name: "Australia", flag: countryToFlag("AU") },
  { code: "DE", dial: "+49", name: "Germany", flag: countryToFlag("DE") },
  { code: "FR", dial: "+33", name: "France", flag: countryToFlag("FR") },
  { code: "SG", dial: "+65", name: "Singapore", flag: countryToFlag("SG") },
  { code: "AE", dial: "+971", name: "UAE", flag: countryToFlag("AE") },
  { code: "BR", dial: "+55", name: "Brazil", flag: countryToFlag("BR") },
];

/** Build country list from libphonenumber-js global, popular first then rest alphabetically */
function getCountryList() {
  const lib = window.libphonenumber;
  if (!lib) return { popular: FALLBACK_COUNTRIES, rest: [] };

  const displayNames = new Intl.DisplayNames(["en"], { type: "region" });
  const popularSet = new Set(POPULAR_COUNTRIES);

  const all = lib.getCountries().map((code) => ({
    code,
    dial: `+${lib.getCountryCallingCode(code)}`,
    name: displayNames.of(code) || code,
    flag: countryToFlag(code),
  }));

  const popular = POPULAR_COUNTRIES.map((code) =>
    all.find((c) => c.code === code),
  ).filter(Boolean);

  const rest = all
    .filter((c) => !popularSet.has(c.code))
    .sort((a, b) => a.name.localeCompare(b.name));

  return { popular, rest };
}

/**
 * Create a custom searchable country code picker.
 * @returns {{ el: HTMLElement, getValue: Function, destroy: Function }}
 */
function createCountryPicker(detectedCountry) {
  const { popular, rest } = getCountryList();
  const allCountries = [...popular, ...rest];
  let selectedCountry =
    (detectedCountry && allCountries.find((c) => c.code === detectedCountry)) ||
    popular.find((c) => c.code === "US") ||
    allCountries[0];
  let isOpen = false;

  // Wrapper
  const wrapper = createElement("div", { className: "sm-country-picker" });

  // Trigger button
  const trigger = createElement("button", { className: "sm-country-trigger" });
  trigger.type = "button";
  function updateTrigger() {
    trigger.textContent = `${selectedCountry.flag} ${selectedCountry.dial}`;
    trigger.title = selectedCountry.name;
  }
  updateTrigger();

  // Dropdown
  const dropdown = createElement("div", { className: "sm-country-dropdown" });
  dropdown.style.display = "none";

  // Search input
  const searchInput = createElement("input", {
    className: "sm-country-search",
    type: "text",
    placeholder: "Search country...",
  });
  dropdown.appendChild(searchInput);

  // Options list
  const optionsList = createElement("div", { className: "sm-country-list" });
  dropdown.appendChild(optionsList);

  function renderOptions(filter) {
    optionsList.innerHTML = "";
    const query = (filter || "").toLowerCase();

    const filtered = query
      ? allCountries.filter(
          (c) =>
            c.name.toLowerCase().includes(query) ||
            c.dial.includes(query) ||
            c.code.toLowerCase().includes(query),
        )
      : null;

    const toRender = filtered || [
      { divider: true, label: "Popular" },
      ...popular,
      { divider: true, label: "All countries" },
      ...rest,
    ];

    toRender.forEach((item) => {
      if (item.divider) {
        const div = createElement(
          "div",
          { className: "sm-country-divider" },
          item.label,
        );
        optionsList.appendChild(div);
        return;
      }
      const opt = createElement("div", { className: "sm-country-option" });
      if (item.code === selectedCountry.code)
        opt.classList.add("sm-country-option--selected");
      opt.textContent = `${item.flag} ${item.code} ${item.dial}`;
      opt.addEventListener("click", () => {
        selectedCountry = item;
        updateTrigger();
        closeDropdown();
      });
      optionsList.appendChild(opt);
    });

    if (filtered && filtered.length === 0) {
      optionsList.appendChild(
        createElement("div", { className: "sm-country-empty" }, "No results"),
      );
    }
  }

  function openDropdown() {
    isOpen = true;
    dropdown.style.display = "";
    searchInput.value = "";
    renderOptions("");
    // Focus search after a tick so dropdown is visible
    setTimeout(() => searchInput.focus(), 0);
  }

  function closeDropdown() {
    isOpen = false;
    dropdown.style.display = "none";
    searchInput.value = "";
  }

  trigger.addEventListener("click", () => {
    if (isOpen) closeDropdown();
    else openDropdown();
  });

  searchInput.addEventListener("input", () => {
    renderOptions(searchInput.value);
  });

  // Close on click outside
  function handleDocClick(e) {
    if (isOpen && !wrapper.contains(e.target)) {
      closeDropdown();
    }
  }
  document.addEventListener("mousedown", handleDocClick);

  // Close on Escape
  function handleDocKey(e) {
    if (isOpen && e.key === "Escape") {
      closeDropdown();
    }
  }
  document.addEventListener("keydown", handleDocKey);

  wrapper.appendChild(trigger);
  wrapper.appendChild(dropdown);

  function getValue() {
    return selectedCountry?.dial || "+1";
  }

  function destroy() {
    document.removeEventListener("mousedown", handleDocClick);
    document.removeEventListener("keydown", handleDocKey);
  }

  return { el: wrapper, getValue, destroy };
}

/**
 * Visitor form component.
 *
 * @param {Object} opts
 * @param {Object} opts.employee  - Employee data { name, employee_type, greeting }
 * @param {Function} opts.onSubmit - Called with { name, email, phone } when form is submitted
 * @returns {{ el: HTMLElement, update: Function, destroy: Function }}
 */
export function createVisitorForm({
  employee,
  detectedCountry,
  onSubmit,
  onThemeToggle,
  getTheme,
}) {
  const form = createElement("div", { className: "sm-visitor-form" });

  // Theme toggle
  let themeBtn = null;
  if (onThemeToggle && getTheme) {
    themeBtn = createElement("button", { className: "sm-theme-toggle" });
    themeBtn.type = "button";
    themeBtn.setAttribute("aria-label", "Toggle theme");
    const updateThemeIcon = () => {
      themeBtn.innerHTML = getTheme() === "light" ? MoonIcon : SunIcon;
    };
    updateThemeIcon();
    themeBtn.addEventListener("click", () => {
      onThemeToggle();
      updateThemeIcon();
    });
    form.appendChild(themeBtn);
  }

  // Avatar
  const avatar = createElement(
    "div",
    { className: "sm-avatar" },
    employee.name.charAt(0),
  );
  form.appendChild(avatar);

  // Name
  form.appendChild(
    createElement("h3", { className: "sm-employee-name" }, employee.name),
  );

  const showForm = employee.show_visitor_form !== false;

  // Fields (only when visitor form is enabled)
  let nameInput, emailInput, phoneInput, countryPicker;
  let emailError, phoneError;

  if (showForm) {
    const fields = createElement("div", { className: "sm-form-fields" });

    // Name input
    nameInput = createElement("input", {
      className: "sm-input",
      type: "text",
      placeholder: "Your name *",
    });
    fields.appendChild(nameInput);

    // Email input
    emailInput = createElement("input", {
      className: "sm-input",
      type: "email",
      placeholder: "Your email *",
    });
    fields.appendChild(emailInput);

    // Email error
    emailError = createElement("p", { className: "sm-field-error" });
    emailError.style.display = "none";
    fields.appendChild(emailError);

    // Phone input group
    const phoneGroup = createElement("div", { className: "sm-phone-group" });

    countryPicker = createCountryPicker(detectedCountry);

    phoneInput = createElement("input", {
      className: "sm-input sm-phone-input",
      type: "tel",
      placeholder: "Phone number *",
    });

    phoneGroup.appendChild(countryPicker.el);
    phoneGroup.appendChild(phoneInput);
    fields.appendChild(phoneGroup);

    // Phone error
    phoneError = createElement("p", { className: "sm-field-error" });
    phoneError.style.display = "none";
    fields.appendChild(phoneError);

    form.appendChild(fields);
  }

  // General error text (for API errors)
  const errorEl = createElement("p", { className: "sm-error-text" });
  errorEl.style.display = "none";
  form.appendChild(errorEl);

  // Submit button — always shown
  const button = createElement(
    "button",
    { className: "sm-button" },
    "Start Conversation",
  );
  button.disabled = showForm; // disabled until form filled (only when form shown); enabled immediately when form hidden
  form.appendChild(button);

  // Handlers
  function clearFieldErrors() {
    if (emailError) emailError.style.display = "none";
    if (phoneError) phoneError.style.display = "none";
  }

  function isFormFilled() {
    if (!showForm) return true;
    return (
      nameInput.value.trim() &&
      emailInput.value.trim() &&
      phoneInput.value.trim()
    );
  }

  function updateButtonState() {
    button.disabled = !isFormFilled();
  }

  function handleStart() {
    if (!showForm) {
      onSubmit({ name: "", email: "", phone: "" });
      return;
    }

    clearFieldErrors();

    const name = nameInput.value.trim();
    const email = emailInput.value.trim();
    const phone = phoneInput.value.trim();
    const dialCode = countryPicker.getValue();

    if (!name) return;

    // Validate email
    if (!isValidEmail(email)) {
      emailError.textContent = "Please enter a valid email address";
      emailError.style.display = "";
      return;
    }

    // Validate phone
    if (!isValidPhone(phone)) {
      phoneError.textContent = "Please enter a valid phone number";
      phoneError.style.display = "";
      return;
    }

    const fullPhone = `${dialCode}${phone.replace(/[\s\-()+]/g, "")}`;
    onSubmit({ name, email, phone: fullPhone });
  }

  function handleKeyDown(e) {
    if (e.key === "Enter") handleStart();
  }

  if (showForm) {
    nameInput.addEventListener("keydown", handleKeyDown);
    emailInput.addEventListener("keydown", handleKeyDown);
    phoneInput.addEventListener("keydown", handleKeyDown);
  }
  button.addEventListener("click", handleStart);

  /**
   * Update form state.
   * @param {{ loading?: boolean, error?: string|null }} state
   */
  function update({ loading, error }) {
    button.textContent = loading ? "Connecting..." : "Start Conversation";
    button.disabled = loading || !isFormFilled();

    if (error) {
      errorEl.textContent = error;
      errorEl.style.display = "";
    } else {
      errorEl.style.display = "none";
    }
  }

  // Live-update button disabled state on input change
  if (showForm) {
    nameInput.addEventListener("input", updateButtonState);
    emailInput.addEventListener("input", updateButtonState);
    phoneInput.addEventListener("input", updateButtonState);
  }

  function destroy() {
    if (showForm) {
      nameInput.removeEventListener("keydown", handleKeyDown);
      emailInput.removeEventListener("keydown", handleKeyDown);
      phoneInput.removeEventListener("keydown", handleKeyDown);
      nameInput.removeEventListener("input", updateButtonState);
      emailInput.removeEventListener("input", updateButtonState);
      phoneInput.removeEventListener("input", updateButtonState);
      countryPicker.destroy();
    }
    button.removeEventListener("click", handleStart);
    form.remove();
  }

  return { el: form, update, destroy };
}
